// The notice that a prompt was adjusted (ai_app_backend #1508; Andy, 4 Nov 2026: "when it's rewritten we need to let
// the user know in a subtle notification that appears then disappears"). v2 may change a person's words as little as
// it can so they meet the content rules, then makes the picture or clip from the changed words. Its answer to placing
// the order (`PlacedOrder`: POST /v2/orders and POST /v2/character-portraits) then says `adjusted: true`, and
// `ladder_warning: true` when the words still count as a refused attempt. The adapters that place an order call
// `tellIfAdjusted` with that answer; the one toast mounted in the root layout (component/PromptAdjustedToast.tsx)
// listens and shows it for a few seconds. The changed words and the rule are never shown. Null (a repeat of the same
// receipt number: the first answer already said it) and false say nothing.

/** What the toast says: always that the prompt was adjusted, and the warning about repeated attempts when v2 asks. */
export type AdjustedNotice = { ladderWarning: boolean }

/** The window event the toast listens for, its `detail` an `AdjustedNotice`. */
export const ADJUSTED_EVENT = 'ai-app:prompt-adjusted'

/** The translation keys of the toast's two sentences (`en` holds the English). */
export const ADJUSTED_KEY = 'promptAdjustedNotice'
export const LADDER_WARNING_KEY = 'promptAdjustedLadderWarning'

/** The notice an order's placing answer asks for, or null when it asks for none (only `adjusted: true` does). */
export function adjustedNotice(placed: unknown): AdjustedNotice | null {
  if (typeof placed !== 'object' || placed === null) return null
  const { adjusted, ladder_warning } = placed as {
    adjusted?: unknown
    ladder_warning?: unknown
  }
  if (adjusted !== true) return null
  return { ladderWarning: ladder_warning === true }
}

/** The translation keys the toast shows for `notice`, in order. */
export const adjustedNoticeKeys = (notice: AdjustedNotice): string[] =>
  notice.ladderWarning ? [ADJUSTED_KEY, LADDER_WARNING_KEY] : [ADJUSTED_KEY]

/** Tells the toast when an order's placing answer says its prompt was adjusted; does nothing otherwise. */
export function tellIfAdjusted(placed: unknown): void {
  const notice = adjustedNotice(placed)
  if (!notice || typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ADJUSTED_EVENT, { detail: notice }))
}
